import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { mediaApi } from '@/api/mediaWrapper';
import type { MediaAssetResponse, MediaTagResponse } from 'we-tree-api-client/v2';
import { preventDialogDismissForHelpPop } from '@/components/help/HelpDot';
import StoryMediaThumb from '@/components/memories/StoryMediaThumb';
import MediaUploadForm from '@/components/media/MediaUploadForm';
import TagFilterDropdown from '@/components/shared/TagFilterDropdown';
import type { MediaTypeFilter, MediaUploadAcceptKinds } from '@/types/media-upload';
import { mimeTypeFilterParam } from '@/utils/mediaUpload';
import { familyHasPaidPlan } from '@/utils/familyPlan';
import { useDomainStore } from '@/hooks/useStores';

import './change-photo-modal.css';

export type { MediaTypeFilter };

export type ChooseMediaSelectMeta = {
  mimeType?: string;
  originalFilename?: string;
};

export interface ChooseMediaModalProps {
  title: string;
  familyId: string;
  /** When set, upload tab uses profile/gallery upload for that member. When omitted, family-level upload. */
  familyMemberId?: string;
  /** `images` (default) for photos/citations; `all` allows video/audio (Family plan). */
  acceptKinds?: MediaUploadAcceptKinds;
  /** When familyMemberId is set, whether upload becomes primary profile photo. Default true. */
  setAsPrimaryProfilePhoto?: boolean;
  /** Asset IDs already attached (e.g. to this memory) — hidden from the gallery picker. */
  excludeMediaAssetIds?: string[];
  isOpen: boolean;
  onClose: () => void;
  onSelect: (mediaAssetId: string, meta?: ChooseMediaSelectMeta) => void;
  onUploadSuccess?: () => void;
}

const ChooseMediaModal: React.FC<ChooseMediaModalProps> = ({
  title,
  familyId,
  familyMemberId,
  acceptKinds = 'images',
  setAsPrimaryProfilePhoto = true,
  excludeMediaAssetIds,
  isOpen,
  onClose,
  onSelect,
  onUploadSuccess
}) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const domainStore = useDomainStore();
  const hasPaidPlan = familyHasPaidPlan(domainStore.currentFamily?.plan);

  const [mode, setMode] = useState<'upload' | 'gallery'>('upload');
  const [items, setItems] = useState<MediaAssetResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [mediaTypeFilter, setMediaTypeFilter] = useState<MediaTypeFilter>(acceptKinds === 'images' ? 'images' : 'all');
  const [tags, setTags] = useState<MediaTagResponse[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [tagMatchAnd, setTagMatchAnd] = useState(false);

  const excludedIds = useMemo(() => new Set(excludeMediaAssetIds ?? []), [excludeMediaAssetIds]);

  const availableItems = useMemo(() => items.filter((asset) => !excludedIds.has(asset.id)), [items, excludedIds]);

  const loadGallery = useCallback(async () => {
    if (!familyId) return;
    setIsLoading(true);
    const result = await mediaApi.searchMediaAssets(familyId, {
      mimeType: mimeTypeFilterParam(mediaTypeFilter),
      tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      tagMatch: tagMatchAnd ? 'and' : 'or',
      page: 1,
      pageSize: 60
    });
    setIsLoading(false);
    if (result.success && result.data) {
      setItems(result.data.items);
      setTotal(result.data.total);
    } else {
      setItems([]);
      setTotal(0);
    }
  }, [familyId, selectedTagIds, tagMatchAnd, mediaTypeFilter]);

  useEffect(() => {
    if (!isOpen || !familyId) return;
    mediaApi.listMediaTags(familyId).then((result) => {
      if (result.success && result.data) setTags(result.data);
      else setTags([]);
    });
  }, [isOpen, familyId]);

  useEffect(() => {
    if (isOpen && mode === 'gallery' && familyId) {
      loadGallery();
    }
  }, [isOpen, mode, familyId, loadGallery]);

  useEffect(() => {
    if (!isOpen) return;
    setMediaTypeFilter(acceptKinds === 'images' ? 'images' : 'all');
  }, [isOpen, acceptKinds]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose]
  );

  const handleGallerySelect = (mediaAssetId: string) => {
    if (excludedIds.has(mediaAssetId)) return;
    const asset = availableItems.find((item) => item.id === mediaAssetId);
    onSelect(mediaAssetId, asset ? { mimeType: asset.mimeType, originalFilename: asset.originalFilename } : undefined);
    onClose();
  };

  const handleUploadSuccess = (asset: MediaAssetResponse) => {
    if (excludedIds.has(asset.id)) {
      onClose();
      return;
    }
    onUploadSuccess?.();
    // Attach/select the new asset — upload alone used to close without calling onSelect
    onSelect(asset.id, { mimeType: asset.mimeType, originalFilename: asset.originalFilename });
    onClose();
  };

  const showVideoAudioFilters = acceptKinds === 'all';
  const excludedCount = items.length - availableItems.length;
  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="v01-modal-overlay v01-modal-overlay--centered">
          <Dialog.Content
            className="v01-modal change-photo-modal__shell"
            aria-describedby={undefined}
            onPointerDownOutside={preventDialogDismissForHelpPop}
            onFocusOutside={preventDialogDismissForHelpPop}
          >
            <div className="change-photo-modal__header">
              <Dialog.Title asChild>
                <h2 className="v01-modal__title">{title}</h2>
              </Dialog.Title>
              <Dialog.Close asChild>
                <button type="button" className="v01-modal__close" aria-label={tCommon('close')}>
                  ×
                </button>
              </Dialog.Close>
            </div>

            <div className="change-photo-modal__tabs">
              <div className="list-seg" role="tablist" aria-label={t('mediaPicker.sourceAria')}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'upload'}
                  className={`list-seg__btn ${mode === 'upload' ? 'list-seg__btn--active' : ''}`}
                  onClick={() => setMode('upload')}
                >
                  {t('mediaPicker.uploadNew')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'gallery'}
                  className={`list-seg__btn ${mode === 'gallery' ? 'list-seg__btn--active' : ''}`}
                  onClick={() => setMode('gallery')}
                >
                  {t('mediaPicker.chooseFromGallery')}
                </button>
              </div>
            </div>

            {mode === 'upload' && (
              <div className="change-photo-modal__upload-panel">
                <MediaUploadForm
                  familyId={familyId}
                  familyMemberId={familyMemberId}
                  acceptKinds={acceptKinds}
                  setAsPrimaryProfilePhoto={setAsPrimaryProfilePhoto}
                  hasPaidPlan={hasPaidPlan}
                  onSuccess={handleUploadSuccess}
                  compact
                />
              </div>
            )}

            {mode === 'gallery' && (
              <div className="change-photo-modal__gallery-panel">
                <div className="change-photo-modal__filter-bar">
                  <div className="list-seg" role="group" aria-label={t('mediaPicker.typeAria')}>
                    <button
                      type="button"
                      className={`list-seg__btn ${mediaTypeFilter === 'all' ? 'list-seg__btn--active' : ''}`}
                      onClick={() => setMediaTypeFilter('all')}
                    >
                      {t('actions.all')}
                    </button>
                    <button
                      type="button"
                      className={`list-seg__btn ${mediaTypeFilter === 'images' ? 'list-seg__btn--active' : ''}`}
                      onClick={() => setMediaTypeFilter('images')}
                    >
                      {t('mediaPicker.images')}
                    </button>
                    {showVideoAudioFilters && (
                      <>
                        <button
                          type="button"
                          className={`list-seg__btn ${mediaTypeFilter === 'video' ? 'list-seg__btn--active' : ''}`}
                          onClick={() => setMediaTypeFilter('video')}
                        >
                          {t('mediaPicker.video')}
                        </button>
                        <button
                          type="button"
                          className={`list-seg__btn ${mediaTypeFilter === 'audio' ? 'list-seg__btn--active' : ''}`}
                          onClick={() => setMediaTypeFilter('audio')}
                        >
                          {t('mediaPicker.audio')}
                        </button>
                      </>
                    )}
                  </div>
                  <div className="change-photo-modal__tag-filter">
                    <TagFilterDropdown
                      id="choose-media-tag-filter"
                      tags={tags}
                      selectedTagIds={selectedTagIds}
                      tagMatchAnd={tagMatchAnd}
                      onSelectedTagsChange={setSelectedTagIds}
                      onTagMatchChange={setTagMatchAnd}
                      ariaLabel={t('mediaPicker.filterByTag')}
                    />
                  </div>
                </div>

                {isLoading ? (
                  <div className="change-photo-modal__loading">{tCommon('loading')}</div>
                ) : availableItems.length === 0 ? (
                  <p className="change-photo-modal__empty">
                    {excludedCount > 0 ? t('mediaPicker.emptyAllAttached') : t('mediaPicker.emptyNone')}
                  </p>
                ) : (
                  <>
                    <p className="change-photo-modal__count">
                      {t('mediaPicker.showing', { count: availableItems.length })}
                      {total > items.length ? t('mediaPicker.ofTotal', { total }) : ''}
                      {excludedCount > 0 ? t('mediaPicker.alreadyAttached', { count: excludedCount }) : ''}
                    </p>
                    <div className="change-photo-modal__grid change-photo-modal__grid--small">
                      {availableItems.map((asset) => (
                        <StoryMediaThumb
                          key={asset.id}
                          familyId={familyId}
                          mediaAssetId={asset.id}
                          mimeType={asset.mimeType}
                          size="40x40"
                          label={asset.originalFilename || t('mediaPicker.selectMedia')}
                          onClick={() => handleGallerySelect(asset.id)}
                          allowSelectWhileProcessing
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ChooseMediaModal;
